import { Button } from '../UI/Button';
import { Tooltip } from '../UI/Tooltip';

interface HeaderProps {
  onShowHelp: () => void;
  onToggleFullscreen: () => void;
  onToggleMobileMenu: () => void;
}

export function Header({ onShowHelp, onToggleFullscreen, onToggleMobileMenu }: HeaderProps) {
  return (
    <header className="fixed top-0 left-0 right-0 h-[60px] z-40 flex items-center justify-between px-3 sm:px-5"
      style={{
        background: 'rgba(10, 10, 15, 0.85)',
        backdropFilter: 'blur(24px)',
        WebkitBackdropFilter: 'blur(24px)',
        borderBottom: '1px solid rgba(255,255,255,0.06)',
        boxShadow: '0 1px 40px rgba(0,0,0,0.4)',
      }}
    >
      {/* Brand + mobile menu */}
      <div className="flex items-center gap-2 sm:gap-3 min-w-0">
        {/* Hamburger — mobile only */}
        <button
          onClick={onToggleMobileMenu}
          className="md:hidden w-9 h-9 flex items-center justify-center rounded-xl text-white/60 hover:text-white hover:bg-white/5 transition-colors flex-shrink-0"
          aria-label="Open test menu"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
          </svg>
        </button>

        <div className="relative w-9 h-9 flex items-center justify-center flex-shrink-0">
          <div className="absolute inset-0 rounded-xl bg-[#00d9ff]/10 blur-md" />
          <svg width="36" height="36" viewBox="0 0 36 36" fill="none" xmlns="http://www.w3.org/2000/svg" className="relative">
            <rect x="3" y="4" width="30" height="22" rx="3" stroke="url(#logoGrad)" strokeWidth="2" fill="none" />
            <rect x="6" y="7" width="24" height="16" rx="1.5" fill="#0a0a0f" />
            <rect x="8" y="9" width="4" height="4" rx="0.5" fill="#00d9ff" opacity="0.9" />
            <rect x="13" y="9" width="4" height="4" rx="0.5" fill="#00ff00" opacity="0.9" />
            <rect x="18" y="9" width="4" height="4" rx="0.5" fill="#ff3366" opacity="0.7" />
            <rect x="23" y="9" width="4" height="4" rx="0.5" fill="#ffaa00" opacity="0.7" />
            <rect x="8" y="14" width="4" height="4" rx="0.5" fill="#aa44ff" opacity="0.7" />
            <rect x="13" y="14" width="4" height="4" rx="0.5" fill="#ffffff" opacity="0.5" />
            <rect x="18" y="14" width="4" height="4" rx="0.5" fill="#00d9ff" opacity="0.6" />
            <rect x="23" y="14" width="4" height="4" rx="0.5" fill="#00ff00" opacity="0.6" />
            <path d="M14 26L13 30H23L22 26" stroke="url(#logoGrad)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            <line x1="11" y1="30" x2="25" y2="30" stroke="url(#logoGrad)" strokeWidth="1.5" strokeLinecap="round" />
            <circle cx="27" cy="27" r="6" fill="#0a0a0f" stroke="#00ff00" strokeWidth="1.5" />
            <path d="M24 27L26 29L30 25" stroke="#00ff00" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            <defs>
              <linearGradient id="logoGrad" x1="3" y1="4" x2="33" y2="30" gradientUnits="userSpaceOnUse">
                <stop stopColor="#00d9ff" />
                <stop offset="1" stopColor="#00ff00" />
              </linearGradient>
            </defs>
          </svg>
        </div>

        <div className="flex flex-col leading-tight min-w-0">
          <h1 className="text-[15px] sm:text-[17px] font-bold tracking-tight truncate">
            <span className="text-gradient">OLED</span>
            <span className="text-white"> Tools</span>
          </h1>
          <span className="block text-[9px] font-semibold uppercase tracking-[0.18em] text-white/40 truncate">
            Burn-In Prevention &amp; Display Testing
          </span>
        </div>
      </div>

      {/* Right controls */}
      <div className="flex items-center gap-2 sm:gap-3 flex-shrink-0">
        <div className="hidden lg:flex items-center gap-2 px-3.5 py-1.5 rounded-xl text-sm text-white/50"
          style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.06)' }}
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-[#00d9ff]/60" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
              d="M3.75 3.75v4.5m0-4.5h4.5m-4.5 0L9 9M3.75 20.25v-4.5m0 4.5h4.5m-4.5 0L9 15M20.25 3.75h-4.5m4.5 0v4.5m0-4.5L15 9m5.25 11.25h-4.5m4.5 0v-4.5m0 4.5L15 15" />
          </svg>
          <span>
            Press{' '}
            <kbd className="px-1.5 py-0.5 rounded-md text-[#00d9ff] font-semibold"
              style={{ background: 'rgba(0,217,255,0.1)', fontSize: '11px' }}>F</kbd>
            {' '}for fullscreen
          </span>
        </div>

        <Tooltip content="Fullscreen (F)">
          <Button variant="tertiary" size="sm" onClick={onToggleFullscreen}
            className="rounded-xl hover:bg-white/5 transition-all duration-200"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                d="M3.75 3.75v4.5m0-4.5h4.5m-4.5 0L9 9M3.75 20.25v-4.5m0 4.5h4.5m-4.5 0L9 15M20.25 3.75h-4.5m4.5 0v4.5m0-4.5L15 9m5.25 11.25h-4.5m4.5 0v-4.5m0 4.5L15 15" />
            </svg>
          </Button>
        </Tooltip>

        <Tooltip content="Help & Shortcuts (?)">
          <Button variant="tertiary" size="sm" onClick={onShowHelp}
            className="rounded-xl hover:bg-white/5 transition-all duration-200"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </Button>
        </Tooltip>
      </div>
    </header>
  );
}
